// src/pages/Products.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
} from "firebase/firestore";
import "../styles/products.css";

const PAGE_SIZE = 12;
const WAPP_NUMBER = "22800000000";
const SALES_EMAIL = "contact@mameche.tg";
const wapp = (txt) => `https://wa.me/${WAPP_NUMBER}?text=${encodeURIComponent(txt)}`;
const mailto = (subject, body) =>
  `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

export default function Products() {
  const { dept } = useParams(); // "medical" | "industrial"
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");

  // Recherche (input contrôlé + légère temporisation)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Tri local (sans changer la requête Firestore)
  const [sortMode, setSortMode] = useState("recent"); // "recent" | "alpha"

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const cursorRef = useRef(null);

  // ========= Chargement Firestore =========
  const loadPage = async (reset = true) => {
    const base = collection(db, "products");
    let q = query(base, where("department", "==", dept), orderBy("createdAt", "desc"), limit(PAGE_SIZE));

    if (selectedCat !== "all") {
      q = query(
        base,
        where("department", "==", dept),
        where("category", "==", selectedCat),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      );
    }
    if (!reset && cursorRef.current) {
      q = query(q, startAfter(cursorRef.current));
    }

    const snap = await getDocs(q);
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (reset) setItems(docs);
    else setItems((prev) => [...prev, ...docs]);

    cursorRef.current = snap.docs[snap.docs.length - 1] || null;
    setHasMore(snap.size === PAGE_SIZE);

    // Catégories (basées sur la page courante – suffisant UX)
    if (reset) {
      const unique = Array.from(
        new Set(docs.map((x) => x.category).filter(Boolean))
      ).sort();
      setCategories(unique);
    }
  };

  useEffect(() => {
    setLoading(true);
    cursorRef.current = null;
    loadPage(true).finally(() => setLoading(false));
  }, [dept, selectedCat]);

  // ========= Filtres + Tri côté client =========
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let arr = !q
      ? items
      : items.filter((x) =>
        `${x.title ?? ""} ${x.ref ?? ""} ${x.desc ?? ""}`
          .toLowerCase()
          .includes(q)
      );

    if (sortMode === "alpha") {
      arr = [...arr].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" })
      );
    } // sinon "recent" = ordre Firestore déjà DESC sur createdAt
    return arr;
  }, [items, search, sortMode]);

  const emptyState = !loading && filtered.length === 0;
  const title = dept === "medical" ? "Catalogue médical" : "Catalogue industriel";

  // Badge de département
  const deptBadge = (
    <span className={`badge ${dept === "medical" ? "bg-success" : "bg-secondary"}`}>
      {dept === "medical" ? "Médical" : "Industriel"}
    </span>
  );

  return (
    <div className="container py-4">
      {/* ===== En-tête + onglets de département ===== */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-3 mb-3">
        <div>
          <h1 className="h3 fw-bold mb-1">{title}</h1>
          <div className="d-flex align-items-center gap-2 text-muted small">
            {deptBadge}
            <span>•</span>
            <span>{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span>
          </div>
        </div>

        {/* Onglets de changement de département */}
        <div className="btn-group" role="group" aria-label="Choix du département">
          <Link
            to="/medical/products"
            className={`btn btn-sm ${dept === "medical" ? "btn-success" : "btn-outline-success"}`}
          >
            Médical
          </Link>
          <Link
            to="/industrial/products"
            className={`btn btn-sm ${dept === "industrial" ? "btn-secondary" : "btn-outline-secondary"}`}
          >
            Industriel
          </Link>
        </div>
      </div>

      {/* ===== Barre de filtres ===== */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-center">
            {/* Recherche */}
            <div className="col-12 col-lg-6">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search" />
                </span>
                <input
                  className="form-control border-start-0"
                  placeholder="Rechercher (nom, réf., description)…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => { setSearchInput(""); setSearch(""); }}
                    title="Effacer"
                  >
                    <i className="bi bi-x" />
                  </button>
                )}
              </div>
            </div>

            {/* Tri */}
            <div className="col-6 col-lg-3">
              <select
                className="form-select"
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
              >
                <option value="recent">Tri : plus récents</option>
                <option value="alpha">Tri : A → Z</option>
              </select>
            </div>

            {/* Catégories (select visible XS / chips visible ≥SM) */}
            <div className="col-6 col-lg-3 d-sm-none">
              <select
                className="form-select"
                value={selectedCat}
                onChange={(e) => setSelectedCat(e.target.value)}
              >
                <option value="all">Toutes catégories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {/* Chips scrollables */}
            <div className="col-12 d-none d-sm-block">
              <div className="d-flex flex-nowrap overflow-auto gap-2 py-1">
                <button
                  className={`btn btn-sm rounded-pill ${selectedCat === "all" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setSelectedCat("all")}
                >
                  Toutes
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    className={`btn btn-sm rounded-pill ${selectedCat === c ? "btn-success" : "btn-outline-success"}`}
                    onClick={() => setSelectedCat(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Skeletons ===== */}
      {loading && (
        <div className="row g-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="col-12 col-md-6 col-xl-4" key={i}>
              <div className="card product-card shadow-sm h-100">
                <div className="product-thumb aspect-3x2 placeholder" />
                <div className="card-body product-body">
                  <div className="placeholder-glow">
                    <span className="placeholder col-3 me-2"></span>
                    <span className="placeholder col-2"></span>
                  </div>
                  <h6 className="mt-2 placeholder-glow"><span className="placeholder col-8"></span></h6>
                  <p className="small text-muted placeholder-glow"><span className="placeholder col-12"></span></p>
                  <div className="d-flex gap-2">
                    <span className="btn btn-outline-primary btn-sm disabled placeholder col-3"></span>
                    <span className="btn btn-primary btn-sm disabled placeholder col-3"></span>
                    <span className="btn btn-outline-secondary btn-sm disabled placeholder col-3"></span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== Liste ===== */}
      {!loading && (
        <>
          {emptyState ? (
            <div className="text-center border rounded-3 p-5 bg-light">
              <div className="mb-2">{deptBadge}</div>
              <h5 className="fw-bold mb-1">Aucun produit trouvé</h5>
              <p className="text-muted mb-3">
                Essaie d’élargir la recherche ou change de catégorie.
              </p>
              <div className="d-inline-flex gap-2">
                <button className="btn btn-outline-secondary" onClick={() => { setSearchInput(""); setSearch(""); }}>
                  Effacer la recherche
                </button>
                {selectedCat !== "all" && (
                  <button className="btn btn-outline-secondary" onClick={() => setSelectedCat("all")}>
                    Toutes les catégories
                  </button>
                )}
                <Link className="btn btn-success" to="/contact">Demander un devis</Link>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {filtered.map((p) => (
                <div className="col-6 col-md-6 col-xl-4" key={p.id}>
                  <div className="card h-100 shadow-sm deal-card">
                    {/* Visuel */}
                    <Link
                      to={`/product/${p.id}`}
                      className="deal-thumb d-block"
                      aria-label={`Voir les détails de ${p.title || "Produit"}`}
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.title || "Produit"} />
                      ) : (
                        <img
                          alt="placeholder"
                          src={
                            "data:image/svg+xml;utf8," +
                            encodeURIComponent(
                              `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='533'>
                 <rect width='100%' height='100%' fill='%23f8f9fa'/>
                 <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
                   fill='%239aa0a6' font-family='Arial' font-size='20'>Image indisponible</text>
               </svg>`
                            )
                          }
                        />
                      )}
                    </Link>

                    <div className="card-body d-flex flex-column">
                      {/* Canaux (style “Warehouse & Online”) */}
                      <div className="deal-channels mb-1">
                        <span className="text-danger fw-semibold">Catalogue</span>
                        <span className="px-1">&amp;</span>
                        <span className="text-primary fw-semibold">En ligne</span>
                      </div>

                      {/* Titre */}
                      <h6 className="deal-title mb-1">
                        <Link className="text-decoration-none" to={`/product/${p.id}`} onClick={() => window.scrollTo(0, 0)}>
                          {p.title || "Produit"}
                        </Link>
                      </h6>

                      {/* Méta */}
                      <ul className="deal-meta small text-muted mb-3">
                        {p.category && <li>{p.category}</li>}
                        <li>Réf. {p.ref || "—"}</li>
                        <li>{dept === "medical" ? "Médical" : "Industriel"}</li>
                      </ul>

                      {/* Bloc “Économisez” si tu as p.save */}
                      {!!p.save && Number(p.save) > 0 && (
                        <div className="deal-save text-success mb-3">
                          <div className="small fw-semibold">Économisez</div>
                          <div className="display-6 fw-bold">{p.save}</div>
                        </div>
                      )}

                      {/* CTAs */}
                      <div className="mt-auto d-grid gap-2">
                        <Link
                          className="btn btn-primary btn-lg"
                          to={`/contact?subjectType=${encodeURIComponent("Demande de devis")}&dept=${dept}&product=${encodeURIComponent(p.title || "")}&ref=${encodeURIComponent(p.ref || "")}`}
                          onClick={() => window.scrollTo(0, 0)}
                        >
                          Demander en ligne
                        </Link>

                        {/* <div className="d-flex gap-2">
                          <a
                            className="btn btn-outline-success btn-sm flex-fill"
                            href={`https://wa.me/22800000000?text=${encodeURIComponent(`Bonjour MAMECHE, je souhaite un devis pour: ${p.title} (${p.ref ?? "—"}).`)}`}
                            target="_blank" rel="noreferrer"
                          >
                            WhatsApp
                          </a>
                          <Link className="btn btn-outline-secondary btn-sm flex-fill" to={`/product/${p.id}`} onClick={() => window.scrollTo(0, 0)}>
                            Détails
                          </Link>
                          <a
                            className="btn btn-outline-dark btn-sm flex-fill"
                            href={`mailto:contact@mameche.tg?subject=${encodeURIComponent(`Demande de devis – ${p.title}`)}&body=${encodeURIComponent(
                              `Bonjour,\n\nJe souhaite un devis pour la référence ${p.ref ?? "—"} (${p.title}).\nQuantité: __\nDélai souhaité: __\n\nCordialement,`
                            )}`}
                          >
                            Email
                          </a>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>

              ))}
            </div>
          )}

          {/* Voir plus */}
          {!emptyState && hasMore && (
            <div className="d-grid mt-4">
              <button
                className="btn btn-outline-secondary"
                disabled={loadingMore}
                onClick={async () => {
                  setLoadingMore(true);
                  await loadPage(false);
                  setLoadingMore(false);
                }}
              >
                {loadingMore ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Chargement…
                  </>
                ) : (
                  "Voir plus"
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
